import { useCallback, useEffect, useState } from 'react';
import {
  type AuditEntry,
  type AuditListResponse,
  listAudit,
  exportAudit,
} from '../services/api';
import { useT } from '../i18n';

// Issue #550: Auditoria LGPD voltou a ser pagina propria no grupo "Gestao"
// (pages/Audit.tsx), visivel para os tenants — AUDIT_ROLES do backend
// (super_user/parceiro/admin/owner/auditor). Antes era tab do AdminPanel
// (issue #546, apenas super_user). O titulo/subtitulo da pagina ficam no
// shell <Page>; aqui so o conteudo (filtros, info LGPD, tabela).

// Acoes efetivamente gravadas no audit_log (call sites em backend/app —
// routers/services/tasks). VALID_ACTIONS (audit_service.py) e a whitelist
// documentada, mas o log real usa a familia document_*/sefaz_*/cert_* etc.
// A lista anterior filtrava acoes que nunca sao gravadas (document_process,
// consent_accepted, login, logout), entao o filtro sempre vinha vazio.
// Entradas gravadas sem organization_id (ex.: dsar_*, cross-org) nao
// aparecem — a query do backend e sempre org-scoped.
const AUDIT_ACTION_GROUPS: { labelKey: string; actions: string[] }[] = [
  {
    labelKey: 'audit.group.documents',
    actions: [
      'document_upload',
      'document_upload_api_v1',
      'document_email_upload',
      'document_upload_duplicate',
      'document_processed',
      'document_field_correction',
      'document_substituted',
      'document_soft_deleted',
      'document_deleted',
      'document_restored',
      'document_purged',
    ],
  },
  {
    labelKey: 'audit.group.sefaz',
    actions: [
      'sefaz_search',
      'sefaz_batch_search',
      'sefaz_batch_upload',
      'sefaz_batch_export',
      'nfse_validate',
    ],
  },
  {
    labelKey: 'audit.group.certificates',
    actions: [
      'cert_upload',
      'cert_view',
      'cert_rotate',
      'cert_delete',
      'cert_use',
      'cert_session_open',
      'cert_consent_grant',
    ],
  },
  {
    labelKey: 'audit.group.org',
    actions: [
      'member_activate',
      'member_deactivate',
      'member_password_reset',
      'org_switch',
      'org_settings_update',
      'impersonation_start',
      'impersonation_exit',
      'api_key_created',
      'api_key_revoked',
    ],
  },
  {
    labelKey: 'audit.group.finance',
    actions: [
      'conciliacao_mapping',
      'conciliacao_suggest',
      'conciliacao_review',
      'conciliacao_export',
      'rpa_credential_save',
      'rpa_credential_delete',
      'rpa_run',
      'rpa_operator_notify',
    ],
  },
  {
    labelKey: 'audit.group.catalogo',
    actions: [
      'catalogo_sugestao_create',
      'catalogo_sugestao_review',
      'catalogo_municipio_write',
      'catalogo_import',
    ],
  },
  {
    labelKey: 'audit.group.other',
    actions: ['onboarding_step', 'onboarding_completed'],
  },
];

// action -> chave i18n (`audit.action.<action>`), usada no filtro e na
// coluna Evento da tabela. Acao desconhecida cai no fallback (valor cru).
const ACTION_LABELS: Record<string, string> = Object.fromEntries(
  AUDIT_ACTION_GROUPS.flatMap((g) => g.actions.map((a) => [a, `audit.action.${a}`])),
);

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('pt-BR');
  } catch {
    return iso;
  }
}

function AuditPanel() {
  const t = useT();
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Issue #550: estado do export assincrono (fetch autenticado + blob).
  const [exporting, setExporting] = useState(false);

  // Filters
  const [filterAction, setFilterAction] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');

  const fetchAudit = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res: AuditListResponse = await listAudit({
        action: filterAction || undefined,
        date_from: filterDateFrom || undefined,
        date_to: filterDateTo || undefined,
        limit: 200,
      });
      setEntries(res.entries);
      setTotal(res.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('audit.error.load'));
    } finally {
      setLoading(false);
    }
  }, [filterAction, filterDateFrom, filterDateTo]);

  useEffect(() => {
    fetchAudit();
  }, [fetchAudit]);

  // Issue #550: export via fetch autenticado + download de blob — <a href>
  // com ?token= nao funciona (backend so le o header Authorization).
  const handleExport = useCallback(
    async (format: 'csv' | 'json') => {
      setExporting(true);
      setError(null);
      try {
        await exportAudit(format, {
          action: filterAction || undefined,
          date_from: filterDateFrom || undefined,
          date_to: filterDateTo || undefined,
        });
      } catch (err) {
        setError(err instanceof Error ? err.message : t('audit.error.export'));
      } finally {
        setExporting(false);
      }
    },
    [filterAction, filterDateFrom, filterDateTo, t],
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-end">
        <div className="flex gap-2">
          <button
            type="button"
            disabled={exporting}
            onClick={() => handleExport('csv')}
            className="rounded-md border border-brand-300 px-3 py-1.5 text-sm font-medium text-brand-700 hover:bg-brand-50 disabled:opacity-50"
          >
            {exporting ? t('audit.exporting') : t('audit.exportCsv')}
          </button>
          <button
            type="button"
            disabled={exporting}
            onClick={() => handleExport('json')}
            className="rounded-md border border-brand-300 px-3 py-1.5 text-sm font-medium text-brand-700 hover:bg-brand-50 disabled:opacity-50"
          >
            {exporting ? t('audit.exporting') : t('audit.exportJson')}
          </button>
        </div>
      </div>

      {/* LGPD info */}
      <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800">
        <p className="font-medium">{t('audit.compliance.title')}</p>
        <ul className="mt-1 list-inside list-disc text-xs text-blue-700">
          <li>{t('audit.compliance.pii')}</li>
          <li>{t('audit.compliance.retention')}</li>
          <li>{t('audit.compliance.dsar')}</li>
        </ul>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="mb-1 block text-xs text-brand-500">{t('audit.filter.eventType')}</label>
          <select
            value={filterAction}
            onChange={(e) => setFilterAction(e.target.value)}
            className="rounded-md border border-brand-300 px-2 py-1.5 text-sm text-brand-800"
          >
            <option value="">{t('audit.filter.all')}</option>
            {AUDIT_ACTION_GROUPS.map((g) => (
              <optgroup key={g.labelKey} label={t(g.labelKey)}>
                {g.actions.map((a) => (
                  <option key={a} value={a}>
                    {t(ACTION_LABELS[a])}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>
        <div>
          <label className="mb-1 block text-xs text-brand-500">{t('audit.filter.from')}</label>
          <input
            type="date"
            value={filterDateFrom}
            onChange={(e) => setFilterDateFrom(e.target.value)}
            className="rounded-md border border-brand-300 px-2 py-1.5 text-sm text-brand-800"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs text-brand-500">{t('audit.filter.to')}</label>
          <input
            type="date"
            value={filterDateTo}
            onChange={(e) => setFilterDateTo(e.target.value)}
            className="rounded-md border border-brand-300 px-2 py-1.5 text-sm text-brand-800"
          />
        </div>
        <button
          type="button"
          onClick={fetchAudit}
          className="rounded-md bg-brand-700 px-4 py-1.5 text-sm font-medium text-white hover:bg-brand-800"
        >
          {t('common.filter')}
        </button>
      </div>

      {error && (
        <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>
      )}

      {loading ? (
        <p className="text-sm text-brand-600">{t('audit.loading')}</p>
      ) : entries.length === 0 ? (
        <p className="text-sm text-brand-500">{t('audit.empty')}</p>
      ) : (
        <>
          <p className="mb-2 text-sm text-brand-500">{t('audit.eventCount', { count: total })}</p>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-brand-200 text-brand-700">
                <tr>
                  <th className="py-2 pr-4">{t('audit.col.date')}</th>
                  <th className="py-2 pr-4">{t('audit.col.event')}</th>
                  <th className="py-2 pr-4">{t('audit.col.resource')}</th>
                  <th className="py-2 pr-4">{t('audit.col.details')}</th>
                  <th className="py-2 pr-4">{t('audit.col.user')}</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry) => (
                  <tr key={entry.id} className="border-b border-brand-100">
                    <td className="py-2 pr-4 text-xs text-brand-500">
                      {formatDate(entry.created_at)}
                    </td>
                    <td className="py-2 pr-4 text-brand-800">
                      {ACTION_LABELS[entry.action] ? t(ACTION_LABELS[entry.action]) : entry.action}
                    </td>
                    <td className="py-2 pr-4 text-brand-600">{entry.resource}</td>
                    <td className="py-2 pr-4 text-xs text-brand-500 max-w-md">
                      {Object.keys(entry.details).length > 0 ? (
                        <pre className="overflow-x-auto text-xs">
                          {JSON.stringify(entry.details, null, 0)}
                        </pre>
                      ) : (
                        t('common.dash')
                      )}
                    </td>
                    <td className="py-2 pr-4 text-xs text-brand-400">
                      {entry.user_id ? entry.user_id.slice(0, 8) + '...' : t('common.dash')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

export default AuditPanel;
